import { baseService } from "../base";

// Types for booking invoice operations
export interface BookingInvoiceService {
  id: string;
  serviceType: string;
  serviceReference: string;
  serviceDate: string;
  duration?: number;
  startTime?: string;
  endTime?: string;
  totalHours: number;
  clientId?: string;
  clientName?: string;
  location?: string;
  vesselId?: string;
  vesselName?: string;
  vesselRate?: number;
  staffIds?: string[];
  staffNames: string[];
  staffRates: number[];
  inventoryIds?: string[];
  inventoryCubicFootage: number;
  suppliesUsed: {
    id: string;
    name: string;
    quantity: number;
    cost: number;
    price: number;
    selected: boolean;
  }[];
  adminFees?: any[];
  travelTime?: number;
  minimumHours?: number;
  selected?: boolean;
  invoiceStatus: 'not_invoiced' | 'invoiced' | 'paid';
  invoiceId?: string | null;
  serviceCharges: number;
  laborCharges: number;
  supplyCharges: number;
  totalAmount: number;
  // New fields from updated backend
  description: string;
  quantity: number;
  rate: number;
}

export interface BookingDetails {
  id: string;
  bookingReference: string;
  createdAt: string;
  client: {
    id: string;
    companyName: string;
    quickBooksId: string;
    travelTime: number;
  };
  location: {
    name: string;
  };
}

export interface BookingServicesResponse {
  booking: BookingDetails;
  services: BookingInvoiceService[];
  summary: {
    totalServices: number;
    totalAmount: number;
    invoicedServices: number;
    pendingServices: number;
  };
}

export interface CreateBookingInvoiceRequest {
  serviceTicketId?: string;
  lineItems: {
    description: string;
    quantity: number;
    unitPrice: number;
    itemId?: string;
  }[];
  invoiceNumber?: string;
  dueDate?: string;
  memo?: string;
}

export interface QuickBooksInvoice {
  id: string;
  docNumber: string;
  customerRef: {
    value: string;
    name: string;
  };
  totalAmt: number;
  balance: number;
  dueDate: string;
  status: string;
  createTime: string;
  lastUpdatedTime: string;
}

// Get all billable services for a booking
export const getBookingServices = async (
  bookingId: string
): Promise<BookingServicesResponse> => {
  const { data } = await baseService.get(
    `/api/quickbooks/bookings/${bookingId}/services`
  );
  return data;
};

// Create invoice for an entire booking
export const createBookingInvoice = async (
  bookingId: string,
  invoiceData: CreateBookingInvoiceRequest
): Promise<QuickBooksInvoice> => {
  const { data } = await baseService.post(
    `/api/quickbooks/bookings/${bookingId}/create-invoice`,
    invoiceData
  );
  return data;
};

// Get all invoices for a booking
export const getBookingInvoices = async (
  bookingId: string
): Promise<QuickBooksInvoice[]> => {
  const { data } = await baseService.get(
    `/api/quickbooks/bookings/${bookingId}/invoices`
  );
  return data;
};

// Sync invoice status from QuickBooks
export const syncInvoiceStatus = async (
  invoiceId: string
): Promise<QuickBooksInvoice> => {
  const { data } = await baseService.post(
    `/api/quickbooks/invoices/${invoiceId}/sync`
  );
  return data;
};

// Get payment link for invoice
export const getInvoicePaymentLink = async (
  invoiceId: string
): Promise<{ paymentUrl: string }> => {
  const { data } = await baseService.get(
    `/api/quickbooks/invoices/${invoiceId}/paymentlink`
  );
  return data;
}; 